import { useTranslation } from 'react-i18next';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TranslateIcon from '@mui/icons-material/Translate';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { SUPPORTED_LANGUAGES } from '@/app/i18n/config';

interface LanguageSwitcherProps {
  /** `glass` sits on a photograph or dark hero; `standard` on a light surface. */
  variant?: 'standard' | 'glass';
  sx?: SxProps<Theme>;
}

/**
 * Every language is listed by its own name and always visible, rather than
 * tucked in a menu behind a globe icon: a visitor who cannot read the current
 * language still has to be able to spot theirs.
 */
export function LanguageSwitcher({ variant = 'standard', sx }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const glass = variant === 'glass';

  return (
    <Box
      sx={[
        { display: 'inline-flex', alignItems: 'center', gap: 1 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <TranslateIcon
        aria-hidden
        fontSize="small"
        sx={{ color: glass ? 'rgba(255, 255, 255, 0.75)' : 'text.secondary' }}
      />
      <ToggleButtonGroup
        exclusive
        size="small"
        value={i18n.resolvedLanguage}
        // Re-selecting the active language passes null; there is always one.
        onChange={(_, code: string | null) => {
          if (code) void i18n.changeLanguage(code);
        }}
        aria-label={t('language.choose')}
        sx={
          glass
            ? {
                bgcolor: 'rgba(15, 23, 42, 0.35)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                '& .MuiToggleButton-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderColor: 'rgba(255, 255, 255, 0.28)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    color: 'grey.900',
                    bgcolor: 'common.white',
                  },
                },
              }
            : undefined
        }
      >
        {SUPPORTED_LANGUAGES.map(({ code, nativeName }) => (
          <ToggleButton
            key={code}
            value={code}
            lang={code}
            sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 600, lineHeight: 1.6 }}
          >
            {nativeName}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
